/**
 * Provisional system names for Field Library rows.
 *
 * `LibraryField.systemName` is the field's stable machine identifier and is
 * curated by the CGT team DIRECTLY IN THE DATABASE — it is absent from the
 * create/update DTOs, so neither this app nor any other API client can set it.
 * Most rows have not been curated yet, and a blank column told the reader
 * nothing, so an uncurated row falls back to a name derived from its label.
 *
 * The derivation deliberately mirrors the slug rules of the curation migration
 * (`20260907120000_curate_library_field_system_names`), so a provisional name is
 * usually the same string curation would land on — which is what makes the
 * fallback useful rather than merely non-empty.
 *
 * It is nonetheless PROVISIONAL and callers must present it as such (see
 * `FieldLibraryPage`, which renders it muted with an explanatory tooltip). It is
 * derived from the label, so it changes when the label is renamed — the exact
 * property a stable identifier must not have. Never integrate against one.
 */

/** Lower-snake-case slug of a field label, matching the curation migration. */
export function provisionalSystemName(label: string): string {
  return label
    .toLowerCase()
    .replace(/\//g, ' ')
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

/** A field's system name and whether it is the curated one or a derived stand-in. */
export interface ResolvedSystemName {
  name: string
  provisional: boolean
}

/**
 * The name to show for a field: the curated one when it exists, otherwise a
 * provisional one derived from the label.
 *
 * `name` is empty only when there is nothing to derive from either (a blank
 * label, which the create form already refuses) — callers should render a
 * placeholder in that case rather than an empty cell.
 */
export function resolveSystemName(field: {
  systemName?: string
  label: string
}): ResolvedSystemName {
  const curated = field.systemName?.trim()
  if (curated) return { name: curated, provisional: false }
  return { name: provisionalSystemName(field.label), provisional: true }
}

// ─── Display form ────────────────────────────────────────────────────────────

/**
 * A system name written the way a spec writes one: `donor_type` → `Donor Type`.
 *
 * Still an identifier — that is the point. The stored form is a lower-snake
 * slug, which reads as machine output and which nobody asked to see raw; the
 * displayed form drops the underscores, capitalizes each word and separates
 * them with a space, so it stays recognisably a NAME while remaining readable
 * at a glance. Run together as `DonorType` it was formal but hard to scan,
 * particularly on the long clinical names this library is full of.
 *
 * **Casing is recovered from the label wherever it can be**, and that is the
 * whole difference between this and a `replace(/_/g, '')`. These names are slugs
 * of clinical labels, and a slug is lowercase — so a naive pass gives back
 * `Cd34PoolableInventoryCount`, `RecipientAboRh`, `Lt3Review`. Every one of
 * those acronyms is still sitting in the label, correctly cased, so the label is
 * the authority rather than a list of acronyms someone has to remember to
 * extend.
 *
 * A word taken from the label is still capitalized on its first letter, so a
 * lowercase `of` in a label does not produce `UnitsofMeasure`; an all-caps
 * acronym is unaffected by that, which is what makes the two rules compose.
 *
 * The stored slug is never lost — callers keep it in a `title` so it stays
 * copyable. This is a display transform, nothing more.
 */
export function formatSystemName(name: string, label?: string): string {
  const slug = name.trim()
  if (!slug) return ''

  // How the label writes each word, keyed by its lowercase form.
  const labelCasing = new Map<string, string>()
  for (const word of (label ?? '').split(/[^A-Za-z0-9]+/)) {
    if (word) labelCasing.set(word.toLowerCase(), word)
  }

  return slug
    .split('_')
    .filter(Boolean)
    .map(token => upperFirst(labelCasing.get(token) ?? token))
    .join(' ')
}

function upperFirst(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1)
}
